import { Box, Grid, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button } from '@components/Button';
import { ToastType } from '@components/Toast';
import { getDB } from '@database';
import { addToBillingQueue, addToCallingQueue } from '@store/Global/service';
import { setWarningDataStore } from '@store/index';
import { OldLoad } from '@store/loadboard';
import { closeModal } from '@store/modals';
import { voidOrder } from '@store/orders';
import { CACHE_DURATION } from '@utils/cacheUtils';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { createSignal, Show } from 'solid-js';

import { AddressLoadModalId } from './OldLoadWarningGrid';

type Props = {
  selectedLoad: OldLoad | undefined;
};

const AddressLoadModal = (props: Props) => {
  const [comment, setComment] = createSignal<string>('');
  const [loading, setLoading] = createSignal<{
    oldLoad: boolean;
    invoice: boolean;
    call: boolean;
  }>({
    oldLoad: false,
    invoice: false,
    call: false,
  });
  const updateOldLoadsAndClose = async () => {
    const db = getDB();
    if (!db || !props.selectedLoad) return;

    const key = 'old-loads';
    const storeEntry = await db.oldLoads.get(key);
    if (!storeEntry) return;

    const filtered = storeEntry.items.data.filter(
      (load) => load.orderId !== props.selectedLoad!.orderId,
    );

    await db.oldLoads.put({
      key,
      cacheDate: Date.now(),
      cacheDuration: CACHE_DURATION.ONE_DAY,
      items: { data: filtered, totalCount: filtered.length },
    });

    setWarningDataStore('oldLoad', 'data', {
      data: filtered,
      totalCount: filtered.length,
    });

    closeModal(AddressLoadModalId);
  };

  const handleVoid = async () => {
    try {
      setLoading((prev) => ({ ...prev, oldLoad: true }));
      if (!props.selectedLoad) return;
      const didVoid = await voidOrder(props.selectedLoad.orderId);
      if (didVoid.isSuccess) {
        await updateOldLoadsAndClose();
      }
    } catch (err) {
      handleToast(ToastType.Caution, 'Failed to void the load.');
    } finally {
      setLoading((prev) => ({ ...prev, oldLoad: false }));
    }
  };

  const handleInvoice = async () => {
    if (!props.selectedLoad) return;
    try {
      setLoading((prev) => ({ ...prev, invoice: true }));
      const res = await addToBillingQueue(props.selectedLoad.loadId);
      if (Boolean(res?.isSuccess)) {
        handleToast(ToastType.Success, 'Added to billing queue');
        await updateOldLoadsAndClose();
      }
    } catch (err) {
      handleToast(ToastType.Caution, 'Failed to invoice customer.');
    } finally {
      setLoading((prev) => ({ ...prev, invoice: false }));
    }
  };

  const handleCall = async () => {
    if (!props.selectedLoad) return;

    if (props.selectedLoad.loadStatus !== 'Empty') {
      handleToast(
        ToastType.Caution,
        'Cannot get paperwork unless status is Empty. Mark as Empty first.',
      );
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, call: true }));
      const res = await addToCallingQueue(props.selectedLoad.loadId, comment());

      if (Boolean(res?.isSuccess)) {
        handleToast(ToastType.Success, 'Added to calling queue');
        await updateOldLoadsAndClose();
      } else {
        handleToast(ToastType.Caution, 'Failed to add to calling queue.');
      }
    } catch (err) {
      handleToast(ToastType.Caution, 'Failed to add to calling queue.');
    } finally {
      setLoading((prev) => ({ ...prev, call: false }));
    }
  };
  return (
    <Box>
      <TextField
        multiline
        label=""
        dataTestId={'comment-textAreaField'}
        rows={4}
        value={comment()}
        maxLength={4000}
        placeholder="Comment..."
        onChange={(value) => {
          setComment(value);
        }}
        class="border-[#a5a5a5]!"
      />
      <Grid container direction={'row'} spacing={2} class="mt-4! h-64">
        <Grid
          item
          size={4}
          justifyContent="space-between"
          class="flex flex-col"
        >
          <h2 class="text-center text-lg font-bold">1.</h2>
          <h4>
            This Load <strong>did not happen</strong>. Void it.{' '}
          </h4>
          <Button
            variant="contained"
            color="primary"
            label="Void"
            class="bg-[#d9534f]! text-white! [&:hover]:bg-[#d9534f]!"
            onClick={handleVoid}
            isLoading={loading().oldLoad}
            disabled={loading().oldLoad}
            dataTestId={formatDataTestId('Void', 'button')}
          />
        </Grid>
        <Grid
          item
          size={4}
          justifyContent="space-between"
          class="flex flex-col"
        >
          <h2 class="text-center text-lg font-bold">2.</h2>
          <h4>
            This Load <strong>did happen</strong> and the customer can be
            invoiced without paperwork. Please invoice customer
          </h4>
          <Button
            variant="outlined"
            color="info"
            label="Invoice"
            onClick={handleInvoice}
            isLoading={loading().invoice}
            disabled={loading().invoice}
            dataTestId={formatDataTestId('Invoice', 'button')}
          />
        </Grid>
        <Grid
          item
          size={4}
          justifyContent="space-between"
          class="flex flex-col"
        >
          <h2 class="text-center text-lg font-bold">3.</h2>
          <Show
            when={props.selectedLoad?.loadStatus !== 'Unassigned'}
            fallback={
              <p class="alert alert-danger bold">
                This Load is unassigned. Please Void if it did not happen or
                assign a carrier.
              </p>
            }
          >
            <p>
              The load <strong>did happen</strong>. Customer <u>cannot</u> be
              invoiced without BOL. Please attempt to contact carrier and get
              documents for processing.
            </p>
            <p>
              <i>We will call up to 3 times before voiding this load.</i>
            </p>
          </Show>
          <Button
            variant="outlined"
            color="primary"
            label="Call"
            disabled={
              props.selectedLoad?.loadStatus === 'Unassigned' || loading().call
            }
            class="bg-[#5bc0de]! text-white! [&:hover]:bg-[#5bc0de]!"
            onClick={handleCall}
            isLoading={loading().call}
            dataTestId={formatDataTestId('Call', 'button')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddressLoadModal;
